import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  sectionMargins,
  sectionPaddings,
  standardColours,
} from '../styles';
import { ArrowLink, Container } from './ui';
import BlogList from './BlogList';

const StyledFeaturedBlogPosts = styled.section`
  ${({ isResourceListing, isBlogDetail }) => {
    if (isResourceListing || isBlogDetail) {
      return css`
        ${sectionMargins('45px', '90px')};
      `;
    } else {
      return css`
        ${sectionPaddings('45px', '90px')};
        background-color: ${standardColours.white};
      `;
    }
  }}
`;

const StyledHeader = styled.header`
  max-width: 880px;
  text-align: center;
  margin: 0 auto 20px;

  ${minBreakpointQuery.small`
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 40px;
  `}
`;

const StyledHeading = styled.h2`
  color: ${brandColours.secondary};
  text-align: center;

  ${({ isBlogDetail }) => {
    if (isBlogDetail) {
      return css`
        ${fluidFontSize(
          '24px',
          '40px',
          breakpointSizes.tiny,
          breakpointSizes.xxxxlarge,
        )};
        font-weight: ${fontWeights.semibold};
      `;
    } else {
      return css`
        ${fontSize(20)};
        font-weight: ${fontWeights.regular};
      `;
    }
  }}
`;

const StyledSubheading = styled.h3`
  margin-top: 15px;
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '24px',
    '40px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  text-align: center;
`;

const StyledBlogList = styled(BlogList)`
  margin-top: 20px;
  padding-bottom: 20px;
  border-bottom: solid 1px ${standardColours.lighterGrey};

  ${minBreakpointQuery.small`
    margin-top: 30px;
    padding-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
    padding-bottom: 40px;
  `}
`;

const StyledLink = styled(ArrowLink)`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}
`;

const FeaturedBlogPosts = ({
  heading,
  subheading,
  items: initialItems,
  isResourceListing,
  isBlogDetail,
}) => {
  const {
    datoCmsBlogListing: { slug },
    allDatoCmsBlog: { nodes },
  } = useStaticQuery(graphql`
    query FeaturedBlogPostsQuery {
      datoCmsBlogListing {
        slug
      }
      allDatoCmsBlog(sort: { meta: { firstPublishedAt: DESC } }, limit: 3) {
        nodes {
          ...BlogCardFragment
        }
      }
    }
  `);

  const items = initialItems && initialItems.length > 0 ? initialItems : nodes;

  return (
    <StyledFeaturedBlogPosts
      isResourceListing={isResourceListing}
      isBlogDetail={isBlogDetail}
    >
      <Container>
        <StyledHeader>
          <StyledHeading isBlogDetail={isBlogDetail}>{heading}</StyledHeading>
          <StyledSubheading>{subheading}</StyledSubheading>
        </StyledHeader>
        <StyledBlogList items={items} isFeatured={true} />
        <StyledLink to={slug}>View all articles</StyledLink>
      </Container>
    </StyledFeaturedBlogPosts>
  );
};

export default FeaturedBlogPosts;
